export const STEP_KEYS = {
  CRITERIA: 'criteria',
  ENVIRONMENT: 'environment',
  SCOPE: 'data-sources',
  CONFIGURE: 'configure',
} as const;

export type StepKey = (typeof STEP_KEYS)[keyof typeof STEP_KEYS];

export const ENVIRONMENT = {
  SANDBOX: 'SANDBOX',
  LIVE: 'LIVE',
} as const;

/**
 * An environment's form value. Non-EP bots only ever use the two `ENVIRONMENT`
 * slugs, but EP bots name their own pipeline stages (QA, UAT, PRE_PROD…) and
 * the value is whatever `releaseEnv` the ladder reports — hence a plain string
 * rather than a union of the two legacy slugs.
 */
export type EnvironmentValue = string;

export const LIVE_START_MODE = {
  NOW: 'now',
  LATER: 'later',
} as const;

export type LiveStartMode = (typeof LIVE_START_MODE)[keyof typeof LIVE_START_MODE];

// One selected metric in the submit payload (mirrors buildPayload in
// CriteriaSelection.vue): full metric objects, not bare keys.
export interface SelectedMetricPayload {
  id: string;
  displayName: string;
  metricKey: string | null;
  type: string;
  metadata: Record<string, unknown>;
}

// Shape submitted to the backend (mirrors legacy buildPayload in CriteriaSelection.vue).
export interface SelectedMetricGroup {
  category: string;
  metrics: SelectedMetricPayload[];
  selectedCriteriaCount: number;
  totalCriteriaCount: number;
}

export interface CreateRunFormData {
  runName: string;
  startTime: string | null;
  endTime: string | null;
  selectedMetrics: SelectedMetricGroup[];
  evaluationBotRefId: string | null;
  historicalBotRefId: string | null;
  evaluationEnvironment: EnvironmentValue;
  historicalEnvironment: EnvironmentValue;
  /** manifestId of the evaluation environment's active build (EP bots only). */
  evaluationManifestId: string | null;
  estimatedCost: string | null;
  estimatedAPICalls: number | null;
  selectedConversationCount: number | null;
  conversationCountThreshold: number | null;
  isLoadingConversationCount: boolean;
  liveStartMode: LiveStartMode;
  sampleRate: number;
  liveStartISO: string | null;
  liveEndISO: string | null;
}

export const INITIAL_FORM_DATA: CreateRunFormData = {
  runName: '',
  startTime: null,
  endTime: null,
  selectedMetrics: [],
  evaluationBotRefId: null,
  historicalBotRefId: null,
  // Seeded from the environment axis once it resolves (EP → lowest pipeline env,
  // non-EP → Staging); see `useCreateRunForm`. Starting empty avoids submitting
  // SANDBOX for an EP bot that has no SANDBOX stage.
  evaluationEnvironment: '',
  historicalEnvironment: '',
  evaluationManifestId: null,
  estimatedCost: null,
  estimatedAPICalls: null,
  selectedConversationCount: null,
  conversationCountThreshold: null,
  isLoadingConversationCount: false,
  liveStartMode: LIVE_START_MODE.NOW,
  sampleRate: 10,
  liveStartISO: null,
  liveEndISO: null,
};
